import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  AccountLockStatusResponse,
  AllLicensesResponse,
  getAllLicenses,
  getBillingInfoById,
  getInvoices,
  getUpgradeableLicenseTypes,
  InvoiceResponse,
  LicenseSummary,
  LicenseTypeResponse,
  updateBillingInfo
} from '~/billing/services/billingService'
import { AccountType } from '~/constants/settings'
import { SocketEvent } from '~/services/socket'
import { BillingInfo } from '~/types/ResponseType'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { useBillingAccounts } from './BillingAccountsContext'
import { useUserContext } from './UserContext'

export interface PermittedCommandsData {
  active_license_types: string[]
  permitted_commands: string[]
  upgrade_messages: {
    [commandName: string]: {
      title: string
      message: string
      lowest_tier: string
    }
  }
}

// Type for billing data that will be shared
export interface BillingData {
  // Stacked licenses - array of all active licenses for this account
  licenses: LicenseSummary[]
  // Full license data with quota usage for the selected account (admin only)
  // Includes: licenses, quota usage (credits, users, period), and plan info
  allLicenses: AllLicensesResponse | null
  allLicensesLoading: boolean
  // Available license types for upgrade (admin only)
  licenseTypes: LicenseTypeResponse[]
  licenseTypesLoading: boolean
  // Invoices for the selected account (admin only)
  invoices: InvoiceResponse[] | null
  invoicesLoading: boolean
  // Account lock status - indicates if account is locked due to credit debt
  lockStatus: AccountLockStatusResponse | null
  lockStatusLoading: boolean
  // Helper to check if account has a specific license type
  hasLicense: (type: AccountType) => boolean
  // Helper to check if account has multiple licenses (admin only)
  hasMultipleLicenses: () => boolean
  // Helper to get the total count of licenses (admin only)
  getLicenseCount: () => number
  billingInfo: BillingInfo | null // Contains is_billing_admin
  // Convenience flag for checking billing admin status
  isBillingAdmin: boolean
  // Convenience flag for checking if account is locked
  isAccountLocked: boolean
  permittedCommands: PermittedCommandsData | null
  error: Error | null
  loaded: boolean
  refetch: () => Promise<void>
  updateBilling: (fields: {
    name?: string
    billing_to?: string
    billing_email?: string
  }) => Promise<void>
  refetchAllLicenses: () => Promise<void>
  refetchLicenseTypes: () => Promise<void>
  refetchInvoices: () => Promise<void>
  refetchLockStatus: () => Promise<void>
}

// Create the context
const BillingContext = createContext<BillingData | null>(null)

// Provider component
interface BillingProviderProps {
  children: ReactNode
}

export function BillingProvider({ children }: BillingProviderProps) {
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null)
  const [permittedCommands, setPermittedCommands] = useState<PermittedCommandsData | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loaded, setLoaded] = useState<boolean>(false)
  const [allLicenses, setAllLicenses] = useState<AllLicensesResponse | null>(null)
  const [allLicensesLoading, setAllLicensesLoading] = useState<boolean>(false)
  const [licenseTypes, setLicenseTypes] = useState<LicenseTypeResponse[]>([])
  const [licenseTypesLoading, setLicenseTypesLoading] = useState<boolean>(false)
  const [invoices, setInvoices] = useState<InvoiceResponse[] | null>(null)
  const [invoicesLoading, setInvoicesLoading] = useState<boolean>(false)
  const billingFetchIdRef = useRef(0)

  // Get user context to watch for authentication changes
  const { isAuthenticated } = useUserContext()

  // Get billing accounts context to watch for selected billing account changes
  const {
    selectedBillingAccountId,
    setSelectedBillingAccountId,
    refetch: refetchBillingAccounts
  } = useBillingAccounts()

  const fetchBillingInfo = useCallback(async () => {
    const fetchId = billingFetchIdRef.current + 1
    billingFetchIdRef.current = fetchId

    if (!selectedBillingAccountId || !isAuthenticated) {
      setBillingInfo(null)
      setPermittedCommands(null)
      setAllLicenses(null)
      setLicenseTypes([])
      setInvoices(null)
      setLoaded(true)
      return
    }

    setLoaded(false)
    setError(null)
    setPermittedCommands(null)
    setAllLicenses(null)
    setLicenseTypes([])
    setInvoices(null)

    try {
      const billing = await getBillingInfoById(selectedBillingAccountId)
      if (fetchId !== billingFetchIdRef.current) {
        return
      }
      setBillingInfo(billing)

      setLoaded(true)
    } catch (err) {
      if (fetchId !== billingFetchIdRef.current) {
        return
      }
      console.warn('Failed to fetch billing information:', err)
      setError(err instanceof Error ? err : new Error('Unknown billing error'))

      // On access denied, refetch the accounts list to get updated permissions
      // then clear selection so auto-select can pick a valid account
      await refetchBillingAccounts()
      setSelectedBillingAccountId(null)
      setLoaded(true)
    }
  }, [
    selectedBillingAccountId,
    isAuthenticated,
    setSelectedBillingAccountId,
    refetchBillingAccounts
  ])

  const updateBilling = useCallback(
    async (fields: { name?: string; billing_to?: string; billing_email?: string }) => {
      if (!billingInfo) return
      const nextName = fields.name ?? billingInfo.name
      const nextBillingTo = fields.billing_to ?? billingInfo.billing_to
      const nextBillingEmail = fields.billing_email ?? billingInfo.billing_email

      await updateBillingInfo(billingInfo.id, nextBillingTo, nextBillingEmail, nextName)
      // Clear cache and force refetch after update
      await fetchBillingInfo()
    },
    [billingInfo, fetchBillingInfo]
  )

  const fetchPermittedCommands = useCallback(
    async (billingAccountId = selectedBillingAccountId) => {
      if (!billingAccountId || !isAuthenticated) return

      try {
        const response = await fetchWrapper(`/api/billing/${billingAccountId}/permitted-commands`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const commandsData = await response.json()
          if (billingAccountId === selectedBillingAccountId) {
            setPermittedCommands(commandsData)
          }
        }
      } catch (err) {
        console.warn('Failed to fetch permitted commands:', err)
      }
    },
    [selectedBillingAccountId, isAuthenticated]
  )

  // Note: This endpoint requires billing admin permission
  // Fetches both licenses and quota usage in a single consolidated call
  const fetchAllLicensesData = useCallback(async () => {
    if (!selectedBillingAccountId || !isAuthenticated) {
      setAllLicenses(null)
      return
    }
    // Skip if not a billing admin - this endpoint requires admin permission
    // Also verify billingInfo matches selectedBillingAccountId to avoid race conditions
    if (!billingInfo?.is_billing_admin || billingInfo.id !== selectedBillingAccountId) {
      setAllLicenses(null)
      return
    }

    setAllLicensesLoading(true)
    try {
      const data = await getAllLicenses(selectedBillingAccountId)
      setAllLicenses(data)
    } catch (err) {
      console.warn('Failed to fetch all licenses:', err)
      setAllLicenses(null)
    } finally {
      setAllLicensesLoading(false)
    }
  }, [selectedBillingAccountId, isAuthenticated, billingInfo?.is_billing_admin, billingInfo?.id])

  // Note: This endpoint requires billing admin permission
  const fetchInvoicesData = useCallback(async () => {
    if (!selectedBillingAccountId || !isAuthenticated) {
      setInvoices(null)
      return
    }
    // Skip if not a billing admin - this endpoint requires admin permission
    // Also verify billingInfo matches selectedBillingAccountId to avoid race conditions
    if (!billingInfo?.is_billing_admin || billingInfo.id !== selectedBillingAccountId) {
      setInvoices(null)
      return
    }

    setInvoicesLoading(true)
    try {
      const data = await getInvoices(selectedBillingAccountId, 10)
      setInvoices(data)
    } catch (err) {
      console.warn('Failed to fetch invoices:', err)
      setInvoices(null)
    } finally {
      setInvoicesLoading(false)
    }
  }, [selectedBillingAccountId, isAuthenticated, billingInfo?.is_billing_admin, billingInfo?.id])

  // Note: License types are only useful for admins who can upgrade plans
  const fetchLicenseTypesData = useCallback(async () => {
    if (!selectedBillingAccountId || !isAuthenticated) {
      setLicenseTypes([])
      return
    }
    // Skip if not a billing admin - only admins can upgrade plans
    // Also verify billingInfo matches selectedBillingAccountId to avoid race conditions
    if (!billingInfo?.is_billing_admin || billingInfo.id !== selectedBillingAccountId) {
      setLicenseTypes([])
      return
    }

    setLicenseTypesLoading(true)
    try {
      const types = await getUpgradeableLicenseTypes(selectedBillingAccountId)
      setLicenseTypes(types)
    } catch (err) {
      console.warn('Failed to fetch license types:', err)
      setLicenseTypes([])
    } finally {
      setLicenseTypesLoading(false)
    }
  }, [selectedBillingAccountId, isAuthenticated, billingInfo?.is_billing_admin, billingInfo?.id])

  const isCurrentBillingInfo = billingInfo?.id === selectedBillingAccountId
  const currentBillingInfo = isCurrentBillingInfo ? billingInfo : null
  const currentAllLicenses = isCurrentBillingInfo ? allLicenses : null
  const currentPermittedCommands = isCurrentBillingInfo ? permittedCommands : null

  // Derive lock status from billing info (no separate API call needed)
  const lockStatus: AccountLockStatusResponse | null = useMemo(() => {
    if (!currentBillingInfo) return null
    return {
      account_locked: currentBillingInfo.account_locked,
      lock_reason: currentBillingInfo.lock_reason ?? undefined,
      credit_debt: currentBillingInfo.credit_debt,
      minimum_credits_to_unlock: currentBillingInfo.minimum_credits_to_unlock
    }
  }, [currentBillingInfo])

  // Lock status is derived from billing info, so loading state follows billing info loading
  const lockStatusLoading = !loaded

  // Refetch lock status by refetching billing info
  const refetchLockStatus = useCallback(async () => {
    await fetchBillingInfo()
  }, [fetchBillingInfo])

  // Listen for BillingInfoUpdated and LicenseUpdated events from WebSocket and refetch billing data
  useEffect(() => {
    const handleBillingUpdate = (...args: unknown[]) => {
      const data = args[0] as { event: string; data: unknown }
      const { event } = data
      if (event === SocketEvent.BillingInfoUpdated) {
        fetchBillingInfo()
      }
      if (event === SocketEvent.LicenseUpdated) {
        fetchAllLicensesData()
        fetchLicenseTypesData()
        // Refetch permitted commands since active_license_types may have changed
        // (e.g., after plan upgrade from Pro to Team)
        fetchPermittedCommands()
      }
    }
    globalEventEmitter.on(AppEvents.SocketEvent, handleBillingUpdate)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, handleBillingUpdate)
    }
  }, [fetchBillingInfo, fetchAllLicensesData, fetchLicenseTypesData, fetchPermittedCommands])

  // Fetch billing data when conditions are met
  useEffect(() => {
    fetchBillingInfo()
  }, [fetchBillingInfo])

  // Fetch permitted commands when billing info changes
  useEffect(() => {
    if (billingInfo?.id === selectedBillingAccountId) {
      fetchPermittedCommands(billingInfo.id)
    }
  }, [billingInfo, selectedBillingAccountId, fetchPermittedCommands])

  // Fetch additional billing data when billing info is loaded
  // Note: licenses (with quota usage), license_types, and invoices are admin-only features
  useEffect(() => {
    if (billingInfo && loaded) {
      // Ensure billingInfo matches the selected billing account to avoid race conditions
      // when switching between accounts with different admin permissions
      if (billingInfo.id !== selectedBillingAccountId) {
        return
      }
      // Only fetch admin-only data if user is a billing admin
      if (billingInfo.is_billing_admin) {
        fetchAllLicensesData()
        fetchLicenseTypesData()
        fetchInvoicesData()
      }
    }
  }, [
    billingInfo,
    loaded,
    selectedBillingAccountId,
    fetchAllLicensesData,
    fetchLicenseTypesData,
    fetchInvoicesData
  ])

  // Get stacked licenses from allLicenses (mapped to LicenseSummary format)
  const licenses = useMemo(
    (): LicenseSummary[] =>
      currentAllLicenses?.licenses.map(license => ({
        instance_id: license.instance_id,
        license_name: license.license_name,
        license_label: license.license_label,
        quota: license.quota,
        start_date: license.start_date,
        end_date: license.end_date,
        subscription_id: license.subscription_id
      })) || [],
    [currentAllLicenses]
  )

  // Convenience flag for checking billing admin status
  const isBillingAdmin = currentBillingInfo?.is_billing_admin ?? false

  // Convenience flag for checking if account is locked (derived from billing info)
  const isAccountLocked = currentBillingInfo?.account_locked ?? false

  // Helper to check if account has a specific license type
  // Checks both admin-only license list AND permittedCommands.active_license_types
  // (which is available to all users, not just billing admins)
  const hasLicense = useCallback(
    (type: AccountType) => {
      // Primary: check detailed license list (admin-only, may not be loaded yet)
      if (licenses.some(license => license.license_name === type)) {
        return true
      }
      // Fallback: check active_license_types from permitted commands (available to all users)
      if (currentPermittedCommands?.active_license_types?.includes(type)) {
        return true
      }
      return false
    },
    [licenses, currentPermittedCommands]
  )

  // Helper to check if billing account has multiple licenses (admin only)
  const hasMultipleLicenses = useCallback(() => {
    return (currentAllLicenses?.total_licenses ?? 0) > 1
  }, [currentAllLicenses])

  // Helper to get the total count of licenses (admin only)
  const getLicenseCount = useCallback(() => {
    return currentAllLicenses?.total_licenses ?? 0
  }, [currentAllLicenses])

  const contextValue: BillingData = useMemo(
    () => ({
      licenses,
      allLicenses: currentAllLicenses,
      allLicensesLoading,
      licenseTypes,
      licenseTypesLoading,
      invoices,
      invoicesLoading,
      lockStatus,
      lockStatusLoading,
      hasLicense,
      hasMultipleLicenses,
      getLicenseCount,
      billingInfo: currentBillingInfo,
      isBillingAdmin,
      isAccountLocked,
      permittedCommands: currentPermittedCommands,
      error,
      loaded,
      refetch: fetchBillingInfo,
      updateBilling,
      refetchAllLicenses: fetchAllLicensesData,
      refetchLicenseTypes: fetchLicenseTypesData,
      refetchInvoices: fetchInvoicesData,
      refetchLockStatus
    }),
    [
      licenses,
      currentAllLicenses,
      allLicensesLoading,
      licenseTypes,
      licenseTypesLoading,
      invoices,
      invoicesLoading,
      lockStatus,
      lockStatusLoading,
      hasLicense,
      hasMultipleLicenses,
      getLicenseCount,
      currentBillingInfo,
      isBillingAdmin,
      isAccountLocked,
      currentPermittedCommands,
      error,
      loaded,
      updateBilling,
      fetchBillingInfo,
      fetchAllLicensesData,
      fetchLicenseTypesData,
      fetchInvoicesData,
      refetchLockStatus
    ]
  )

  return <BillingContext.Provider value={contextValue}>{children}</BillingContext.Provider>
}

// Hook to use the context
export function useBillingContext(): BillingData {
  const context = useContext(BillingContext)
  if (!context) {
    throw new Error('useBillingContext must be used within a BillingProvider')
  }
  return context
}

// Optional variant for isolated renders (e.g. Storybook stories) that may not
// mount the full app provider tree.
export function useOptionalBillingContext(): BillingData | null {
  return useContext(BillingContext)
}
